import {
  DeleteOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  LockOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Button, Modal, Spin, Tag, message } from 'antd';
import FileDownload from 'js-file-download';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import OpenClassAPI from '../OpenClassAPI';
import CertificatePdfUpload from './CertificatePdfUpload';

export type BesaccCertificatePreview = {
  attendeeName: string;
  fileName: string;
  registrationId: number;
  sentAt?: string | null;
};

// Without the two handlers the modal is read only
type BesaccCertificatePreviewModalProps = {
  certificate?: BesaccCertificatePreview;
  onClose: () => void;
  onRemove?: (registrationId: number) => Promise<boolean>;
  onReplace?: (registrationId: number, file: File) => Promise<boolean>;
};

const BesaccCertificatePreviewModal: React.FC<
  BesaccCertificatePreviewModalProps
> = ({ certificate, onClose, onRemove, onReplace }) => {
  const { t } = useTranslation();
  const [blob, setBlob] = useState<Blob>();
  const [objectUrl, setObjectUrl] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!certificate) return;
    let active = true;
    let nextUrl: string | undefined;
    setLoading(true);
    setLoadFailed(false);
    setBlob(undefined);
    setObjectUrl(undefined);
    OpenClassAPI.getBesaccCertificate(certificate.registrationId)
      .then((pdf) => {
        if (!active) return;
        nextUrl = URL.createObjectURL(pdf);
        setBlob(pdf);
        setObjectUrl(nextUrl);
      })
      .catch(() => {
        if (active) {
          setLoadFailed(true);
          message.error(t('components.BesaccCertificatePreview.loadFailed'));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      if (nextUrl) URL.revokeObjectURL(nextUrl);
    };
  }, [certificate, t]);

  if (!certificate) return null;
  const locked = Boolean(certificate.sentAt);
  const editable = !locked && onRemove && onReplace;

  const replace = async (file: File) => {
    setSaving(true);
    try {
      await onReplace?.(certificate.registrationId, file);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      if (await onRemove?.(certificate.registrationId)) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      destroyOnClose
      footer={
        <div className="flex items-center justify-between gap-4">
          <div className="flex gap-2">
            {editable ? (
              <>
                <CertificatePdfUpload onAccepted={replace}>
                  <Button disabled={saving} icon={<UploadOutlined />}>
                    {t('components.BesaccCertificatePreview.replace')}
                  </Button>
                </CertificatePdfUpload>
                <Button
                  danger
                  disabled={saving}
                  icon={<DeleteOutlined />}
                  onClick={remove}>
                  {t('components.BesaccCertificatePreview.remove')}
                </Button>
              </>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button
              disabled={!blob}
              icon={<DownloadOutlined />}
              onClick={() => blob && FileDownload(blob, certificate.fileName)}>
              {t('components.BesaccCertificatePreview.download')}
            </Button>
            <Button type="primary" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      }
      open
      title={
        <div className="flex items-start justify-between gap-4 pr-8">
          <div>
            <div className="flex items-center gap-2 text-base font-semibold text-safeLifeDarker">
              <FilePdfOutlined />
              {certificate.attendeeName}
            </div>
            <div className="mt-1 text-xs font-normal text-grayMedium">
              {certificate.fileName}
            </div>
          </div>
          <Tag
            className={`m-0 rounded-full border-0 ${
              locked
                ? 'bg-blueGrayLight text-blueGrayDark'
                : 'bg-safeLifeLightest text-safeLifeDarker'
            }`}>
            {locked ? <LockOutlined className="mr-1" /> : null}
            {t(
              locked
                ? 'components.BesaccCertificatePreview.sent'
                : 'components.BesaccCertificatePreview.readyToReview',
            )}
          </Tag>
        </div>
      }
      width="92vw"
      onCancel={onClose}>
      <div className="flex h-[72vh] items-center justify-center overflow-hidden rounded border border-solid border-grayLight bg-blueGrayLightest">
        {loading ? <Spin size="large" /> : null}
        {!loading && objectUrl ? (
          <iframe
            className="h-full w-full border-0 bg-white"
            src={objectUrl}
            title={t('components.BesaccCertificatePreview.documentTitle', {
              name: certificate.attendeeName,
            })}
          />
        ) : null}
        {!loading && loadFailed ? (
          <div className="max-w-sm text-center text-grayMedium">
            {t('components.BesaccCertificatePreview.loadFailed')}
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default BesaccCertificatePreviewModal;
